import { useContext } from 'react'
import { useQuery, useMutation, useQueryClient } from 'react-query'
import { apiGet, apiPost, apiPatch, apiDelete } from 'api'
import AuthContext from 'context/auth-context'
import UserContext from 'context/user-context'

// User-defined account rollups (e.g. object codes 0110/0120/0130 grouped as
// "0100 BUDGET CONTROL SAL"). Scoped server-side to the caller's municipality.
//
// Pass `groupingId` for one taxonomy's groups; omit it for all of them, which is what a screen
// labelling a group needs when the group may not be in the grouping being reported under.

const GROUPS_KEY = 'account-groups'

const getAccountGroups = async (token, municipalityId, groupingId) =>
  apiGet({
    url: `municipalities/${municipalityId}/account_groups`,
    token,
    params: groupingId ? { account_grouping_id: groupingId } : undefined
  })

const getAccountGroup = async (token, id) => apiGet({ url: `account_groups/${id}`, token })

const createAccountGroup = async (token, municipalityId, params) =>
  apiPost({
    url: `municipalities/${municipalityId}/account_groups`,
    token,
    objectName: 'account_group',
    params
  })

const updateAccountGroup = async (token, id, params) =>
  apiPatch({ url: `account_groups/${id}`, token, objectName: 'account_group', params })

const deleteAccountGroup = async (token, id) => apiDelete({ url: `account_groups/${id}`, token })

export const useAccountGroups = ({ groupingId, options } = {}) => {
  const token = useContext(AuthContext)
  const { municipality_id } = useContext(UserContext)

  return useQuery(
    [GROUPS_KEY, municipality_id, groupingId || 'all'],
    () => getAccountGroups(token, municipality_id, groupingId),
    { ...options }
  )
}

export const useAccountGroup = ({ id, options } = {}) => {
  const token = useContext(AuthContext)

  return useQuery([GROUPS_KEY, 'detail', id], () => getAccountGroup(token, id), {
    enabled: Boolean(id),
    ...options
  })
}

// Editing a group changes how every chart, pivot, and data table buckets its
// dimension, so chart data has to be refetched alongside the group list itself.
const useInvalidatingMutation = (mutationFn, options) => {
  const queryClient = useQueryClient()

  return useMutation(mutationFn, {
    ...options,
    onSuccess: (data, variables) => {
      queryClient.invalidateQueries(GROUPS_KEY)
      queryClient.invalidateQueries('account-groupings')
      queryClient.invalidateQueries('dashboard-chart-data')
      queryClient.invalidateQueries('report-chart-data')
      queryClient.invalidateQueries('transactions')
      options?.onSuccess?.(data, variables)
    }
  })
}

export const useCreateAccountGroup = ({ options } = {}) => {
  const token = useContext(AuthContext)
  const { municipality_id } = useContext(UserContext)

  return useInvalidatingMutation(
    (params) => createAccountGroup(token, municipality_id, params),
    options
  )
}

export const useUpdateAccountGroup = ({ options } = {}) => {
  const token = useContext(AuthContext)

  return useInvalidatingMutation(({ id, params }) => updateAccountGroup(token, id, params), options)
}

export const useDeleteAccountGroup = ({ options } = {}) => {
  const token = useContext(AuthContext)

  return useInvalidatingMutation((id) => deleteAccountGroup(token, id), options)
}
